import { create } from 'zustand';
import { Pilgrim, PilgrimSchema } from '../types/pilgrim';
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  writeBatch,
  query,
  where,
  onSnapshot,
  getDocs,
  orderBy,
  updateDoc
} from 'firebase/firestore';
import { firestore, COLLECTIONS } from '../lib/firebase/config';
import { normalizePassportForMatch } from '../utils/passportUtils';
import { createDispute } from './pilgrimGuide/actions/disputeActions';

type PilgrimsState = {
  pilgrims: Record<string, Pilgrim>;
  loading: boolean;
  error: string | null;
  initialized: boolean;
  initializeListener: () => void;
  uploadPilgrims: (data: PilgrimSchema[]) => Promise<{ message: string }>;
  updatePilgrims: (data: PilgrimSchema[]) => Promise<{ message: string }>;
  deletePilgrim: (pilgrimId: string) => Promise<void>;
  deleteAll: () => Promise<void>;
  searchPilgrims: (searchTerm: string) => Promise<void>;
  updateStore: (data: Record<string, Pilgrim>) => void;
};

// Constants
const BATCH_SIZE = 450; // Maximum batch size for Firestore
const CONCURRENT_BATCHES = 3; // Number of concurrent batch operations
const PASSPORT_MATCH_BATCH_SIZE = 30; // Firestore "in" query limit

const resolveWaitingIdentificationLinks = async (passportToPilgrim: Map<string, Pilgrim>) => {
  if (passportToPilgrim.size === 0) return;

  const passportKeys = Array.from(passportToPilgrim.keys());
  const chunks = [];

  for (let i = 0; i < passportKeys.length; i += PASSPORT_MATCH_BATCH_SIZE) {
    chunks.push(passportKeys.slice(i, i + PASSPORT_MATCH_BATCH_SIZE));
  }

  for (const chunk of chunks) {
    const linksRef = collection(firestore, 'PilgrimGuideLinks');
    const linksQuery = query(linksRef, where('passportNumberNormalized', 'in', chunk));
    const linksSnapshot = await getDocs(linksQuery);

    if (linksSnapshot.empty) continue;

    const waitingLinksByPassport = new Map<string, Array<{ ref: any; data: any }>>();

    for (const linkDoc of linksSnapshot.docs) {
      const linkData = linkDoc.data();
      if (linkData.status !== 'waiting_identification') continue;

      const matchKey = normalizePassportForMatch(
        linkData.passportNumberNormalized || linkData.passportNumber || ''
      );
      if (!passportToPilgrim.has(matchKey)) continue;

      const group = waitingLinksByPassport.get(matchKey) || [];
      group.push({ ref: linkDoc.ref, data: linkData });
      waitingLinksByPassport.set(matchKey, group);
    }

    if (waitingLinksByPassport.size === 0) continue;

    let batch = writeBatch(firestore);
    let batchOperations = 0;

    for (const [matchKey, waitingLinks] of waitingLinksByPassport.entries()) {
      const pilgrimData = passportToPilgrim.get(matchKey);
      if (!pilgrimData) continue;

      const sortedLinks = [...waitingLinks].sort(
        (a, b) => (a.data.createdAt || 0) - (b.data.createdAt || 0)
      );

      const uniqueGuideLinks = new Map<string, any>();
      for (const link of sortedLinks) {
        const guideId = link.data.guideId;
        if (guideId && !uniqueGuideLinks.has(guideId)) {
          uniqueGuideLinks.set(guideId, link.data);
        }
      }

      const uniqueGuideIds = Array.from(uniqueGuideLinks.keys());
      let disputeId: string | null = null;

      if (uniqueGuideIds.length > 1) {
        const primaryLink = uniqueGuideLinks.get(uniqueGuideIds[0]);
        if (primaryLink) {
          for (let i = 1; i < uniqueGuideIds.length; i++) {
            disputeId = await createDispute(primaryLink, uniqueGuideIds[i], pilgrimData.id);
          }
        }
      }

      const linkStatus = disputeId ? 'disputed' : 'approved';

      for (const link of sortedLinks) {
        const linkData = link.data;

        const updatedLink = {
          pilgrimId: pilgrimData.id,
          pilgrimName: pilgrimData.Name ?? 'Unknown',
          passportNumber: pilgrimData.Passport_Number ?? linkData.passportNumber ?? 'Unknown',
          passportNumberNormalized: matchKey,
          gender: pilgrimData.Gender ? pilgrimData.Gender.toLowerCase() : 'unknown',
          residenceCountry: pilgrimData.Residence_Country ?? null,
          packageName: pilgrimData.package_nickName ?? null,
          pilgrimType: pilgrimData.Pilgrim_Type ?? null,
          nusukGroup: pilgrimData.nusuk_group ?? null,
          email: pilgrimData.Email ? String(pilgrimData.Email).trim() : null,
          mobileNumber: pilgrimData.Mobile_Number ? String(pilgrimData.Mobile_Number).trim() : null,
          status: linkStatus,
          disputeId: disputeId || null,
          updatedAt: Date.now(),
        };

        batch.update(link.ref, updatedLink);
        batchOperations++;

        const selectorRef = collection(firestore, 'PilgrimSelector');
        const selectorQuery = query(selectorRef, where('pilgrimId', '==', pilgrimData.id));
        const selectorSnapshot = await getDocs(selectorQuery);

        if (!selectorSnapshot.empty) {
          const selectorDoc = selectorSnapshot.docs[0];
          const selectorData = selectorDoc.data();

          if (updatedLink.status === 'approved' || selectorData.guideId === linkData.guideId) {
            batch.update(selectorDoc.ref, {
              guideId: linkData.guideId,
              guideName: linkData.guideName ?? 'Unknown Guide',
              organizerId: linkData.organizerId,
              organizerName: linkData.organizerName ?? 'Unknown Organizer',
              'guideData.name': linkData.guideName ?? 'Unknown Guide',
              'guideData.organizerId': linkData.organizerId,
              'organizerData.name': linkData.organizerName ?? 'Unknown Organizer',
              status: 'active',
              updatedAt: Date.now()
            });
            batchOperations++;
          }
        }

        const operationListRef = collection(firestore, 'operationlist');
        const operationQuery = query(operationListRef, where('pilgrimId', '==', pilgrimData.id));
        const operationSnapshot = await getDocs(operationQuery);

        if (operationSnapshot.empty) {
          const operationRef = doc(collection(firestore, 'operationlist'));
          batch.set(operationRef, {
            id: operationRef.id,
            pilgrimId: pilgrimData.id,
            pilgrimName: pilgrimData.Name ?? 'Unknown',
            passportNumber: pilgrimData.Passport_Number ?? 'Unknown',
            gender: pilgrimData.Gender ? pilgrimData.Gender.toLowerCase() : 'unknown',
            residenceCountry: pilgrimData.Residence_Country ?? null,
            packageName: pilgrimData.package_nickName ?? null,
            guideId: linkData.guideId,
            guideName: linkData.guideName ?? 'Unknown Guide',
            organizerId: linkData.organizerId,
            organizerName: linkData.organizerName ?? 'Unknown Organizer',
            operatorId: null,
            operatorName: null,
            operatorOrganizerId: null,
            operatorOrganizerName: null,
            syncedAt: Date.now(),
            isOwner: linkData.isOwner === false ? false : null
          });
          batchOperations++;
        } else {
          operationSnapshot.docs.forEach(operationDoc => {
            batch.update(operationDoc.ref, {
              pilgrimName: pilgrimData.Name ?? 'Unknown',
              passportNumber: pilgrimData.Passport_Number ?? 'Unknown',
              gender: pilgrimData.Gender ? pilgrimData.Gender.toLowerCase() : 'unknown',
              residenceCountry: pilgrimData.Residence_Country ?? null,
              packageName: pilgrimData.package_nickName ?? null,
              guideId: linkData.guideId,
              guideName: linkData.guideName ?? 'Unknown Guide',
              organizerId: linkData.organizerId,
              organizerName: linkData.organizerName ?? 'Unknown Organizer',
              syncedAt: Date.now(),
              isOwner: linkData.isOwner === false ? false : null
            });
            batchOperations++;
          });
        }

        if (batchOperations >= 450) {
          await batch.commit();
          batch = writeBatch(firestore);
          batchOperations = 0;
        }
      }
    }

    if (batchOperations > 0) {
      await batch.commit();
    }
  }
};

export const usePilgrimsStore = create<PilgrimsState>((set, get) => ({
  pilgrims: {},
  loading: false,
  error: null,
  initialized: false,

  initializeListener: () => {
    if (get().initialized) return;

    set({ loading: true, error: null });
    console.log('Initializing pilgrims store - fetching all pilgrims at once');

    // Create query to fetch all pilgrims at once, ordered by createdAt
    const q = query(
      collection(firestore, COLLECTIONS.PILGRIMS),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        console.log(`Loaded ${snapshot.docs.length} pilgrims at once`);
        const pilgrims: Record<string, Pilgrim> = {};
        snapshot.forEach((doc) => {
          pilgrims[doc.id] = {
            ...doc.data(),
            id: doc.id,
          } as Pilgrim;
        });

        set({ 
          pilgrims, 
          initialized: true,
          loading: false
        });
      },
      (error) => {
        console.error('Error in pilgrims listener:', error);
        set({ error: error.message, initialized: false, loading: false });
      }
    );

    return () => {
      console.log('Unsubscribing from pilgrims listener');
      unsubscribe();
    };
  },

  updateStore: (data) => {
    set({ pilgrims: data });
  },

  uploadPilgrims: async (data: PilgrimSchema[]) => {
    set({ loading: true, error: null });
    try {
      // Split data into chunks of BATCH_SIZE
      const chunks = [];
      for (let i = 0; i < data.length; i += BATCH_SIZE) {
        chunks.push(data.slice(i, i + BATCH_SIZE));
      }

      let totalProcessed = 0;
      const processChunk = async (chunk: PilgrimSchema[]) => {
        const batch = writeBatch(firestore);
        const passportToPilgrim = new Map<string, Pilgrim>();
        
        chunk.forEach((pilgrimData) => {
          const docRef = doc(collection(firestore, COLLECTIONS.PILGRIMS));
          const pilgrim: Pilgrim = {
            ...pilgrimData,
            id: docRef.id,
            createdAt: Date.now(),
            updatedAt: Date.now(),
          };
          batch.set(docRef, pilgrim);

          const matchKey = normalizePassportForMatch(pilgrimData.Passport_Number || '');
          if (matchKey) {
            passportToPilgrim.set(matchKey, pilgrim);
          }
        });

        await batch.commit();
        totalProcessed += chunk.length;

        if (passportToPilgrim.size > 0) {
          try {
            await resolveWaitingIdentificationLinks(passportToPilgrim);
          } catch (error) {
            console.error('Error resolving waiting identification links:', error);
          }
        }
        
        // Update progress
        const progress = Math.round((totalProcessed / data.length) * 100);
        console.log(`Upload progress: ${progress}%`);
      };

      // Process chunks with controlled concurrency
      for (let i = 0; i < chunks.length; i += CONCURRENT_BATCHES) {
        const batchPromises = chunks
          .slice(i, i + CONCURRENT_BATCHES)
          .map(chunk => processChunk(chunk));
        await Promise.all(batchPromises);
      }

      return {
        message: `Successfully added ${data.length} pilgrims`,
      };
    } catch (error: any) {
      console.error('Error uploading pilgrims:', error);
      set({ error: error.message, loading: false });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  updatePilgrims: async (data: PilgrimSchema[]) => {
    set({ loading: true, error: null });
    try {
      // Split data into chunks
      const chunks = [];
      for (let i = 0; i < data.length; i += BATCH_SIZE) {
        chunks.push(data.slice(i, i + BATCH_SIZE));
      }

      let totalProcessed = 0;
      let totalUpdated = 0;

      const processChunk = async (chunk: PilgrimSchema[]) => {
        const batch = writeBatch(firestore);
        
        for (const pilgrimData of chunk) {
          // Find existing pilgrim by Serial Number
          const q = query(
            collection(firestore, COLLECTIONS.PILGRIMS),
            where('Serial_Number', '==', pilgrimData.Serial_Number)
          );
          
          const snapshot = await getDocs(q);
          
          if (!snapshot.empty) {
            const docRef = snapshot.docs[0].ref;
            const updateData = {
              ...pilgrimData,
              // Explicitly handle shifting_makkah_roomType
              shifting_makkah_roomType: pilgrimData.shifting_makkah_roomType === undefined ? '' : pilgrimData.shifting_makkah_roomType,
              updatedAt: Date.now()
            };
            batch.update(docRef, updateData);
            totalUpdated++;
          }
        }

        await batch.commit();
        totalProcessed += chunk.length;
        
        // Update progress
        const progress = Math.round((totalProcessed / data.length) * 100);
        console.log(`Update progress: ${progress}%`);
      };

      // Process chunks with controlled concurrency
      for (let i = 0; i < chunks.length; i += CONCURRENT_BATCHES) {
        const batchPromises = chunks
          .slice(i, i + CONCURRENT_BATCHES)
          .map(chunk => processChunk(chunk));
        await Promise.all(batchPromises);
      }

      return {
        message: `Successfully updated ${totalUpdated} pilgrims`,
      };
    } catch (error: any) {
      console.error('Error updating pilgrims:', error);
      set({ error: error.message, loading: false });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  deletePilgrim: async (pilgrimId: string) => {
    set({ loading: true, error: null });
    try {
      const batch = writeBatch(firestore);

      // Delete pilgrim document
      const pilgrimRef = doc(firestore, COLLECTIONS.PILGRIMS, pilgrimId);
      batch.delete(pilgrimRef);

      // Delete related links
      const linksRef = collection(firestore, 'PilgrimGuideLinks');
      const linksQuery = query(linksRef, where('pilgrimId', '==', pilgrimId));
      const linksSnapshot = await getDocs(linksQuery);
      linksSnapshot.forEach(doc => {
        batch.delete(doc.ref);
      });

      // Delete related disputes
      const disputesRef = collection(firestore, 'Disputes');
      const disputesQuery = query(disputesRef, where('pilgrimId', '==', pilgrimId));
      const disputesSnapshot = await getDocs(disputesQuery);
      disputesSnapshot.forEach(doc => {
        batch.delete(doc.ref);
      });

      // Delete related guide bans
      const guideBansRef = collection(firestore, 'GuideBans');
      const guideBansQuery = query(guideBansRef, where('pilgrimId', '==', pilgrimId));
      const guideBansSnapshot = await getDocs(guideBansQuery);
      guideBansSnapshot.forEach(doc => {
        batch.delete(doc.ref);
      });

      // Delete related ownership records
      const ownershipRef = collection(firestore, 'PilgrimOwnership');
      const ownershipQuery = query(ownershipRef, where('pilgrimId', '==', pilgrimId));
      const ownershipSnapshot = await getDocs(ownershipQuery);
      ownershipSnapshot.forEach(doc => {
        batch.delete(doc.ref);
      });

      // Delete from hotel accommodation
      const hotelTablesRef = collection(firestore, 'AccommodationTables');
      const hotelTablesSnapshot = await getDocs(hotelTablesRef);
      
      hotelTablesSnapshot.forEach(tableDoc => {
        const table = tableDoc.data();
        let updated = false;
        
        // Update rooms and beds
        const updatedRooms = table.rooms.map(room => {
          const updatedBeds = room.beds.map(bed => {
            if (bed.assignments) {
              bed.assignments = bed.assignments.filter(a => a.pilgrimId !== pilgrimId);
              if (bed.assignments.length === 0) {
                room.occupiedBeds--;
                updated = true;
              }
            }
            return bed;
          });
          return { ...room, beds: updatedBeds };
        });

        if (updated) {
          batch.update(tableDoc.ref, { rooms: updatedRooms });
        }
      });

      // Delete from camp accommodation
      const campTablesRef = collection(firestore, 'CampAccommodationTables');
      const campTablesSnapshot = await getDocs(campTablesRef);
      
      campTablesSnapshot.forEach(tableDoc => {
        const table = tableDoc.data();
        let updated = false;

        const updatedTents = table.tents.map(tent => {
          const updatedHalls = tent.halls.map(hall => {
            const updatedBeds = hall.beds.map(bed => {
              if (bed.pilgrimId === pilgrimId) {
                updated = true;
                hall.occupiedBeds--;
                tent.occupiedCapacity--;
                return {
                  pilgrimId: null,
                  pilgrimName: null,
                  gender: null,
                  country: null,
                  guideId: null,
                  guideName: null,
                  packageName: null
                };
              }
              return bed;
            });
            return { ...hall, beds: updatedBeds };
          });
          return { ...tent, halls: updatedHalls };
        });

        if (updated) {
          batch.update(tableDoc.ref, { 
            tents: updatedTents,
            occupiedCapacity: table.occupiedCapacity - 1
          });
        }
      });

      // Commit all changes
      await batch.commit();

      // Update local state
      set(state => {
        const newPilgrims = { ...state.pilgrims };
        delete newPilgrims[pilgrimId];
        return { pilgrims: newPilgrims, loading: false };
      });
    } catch (error: any) {
      console.error('Error deleting pilgrim:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  deleteAll: async () => {
    set({ loading: true, error: null });
    try {
      const snapshot = await getDocs(collection(firestore, COLLECTIONS.PILGRIMS));
      
      // Process deletion in batches
      const chunks = [];
      const docs = snapshot.docs;
      for (let i = 0; i < docs.length; i += BATCH_SIZE) {
        chunks.push(docs.slice(i, i + BATCH_SIZE));
      }

      for (let i = 0; i < chunks.length; i += CONCURRENT_BATCHES) {
        const batch = writeBatch(firestore);
        const currentChunks = chunks.slice(i, i + CONCURRENT_BATCHES);
        
        currentChunks.flat().forEach((doc) => {
          batch.delete(doc.ref);
        });

        await batch.commit();
      }

      set({ pilgrims: {}, loading: false });
    } catch (error: any) {
      console.error('Error deleting all pilgrims:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  searchPilgrims: async (searchTerm: string) => {
    set({ loading: true, error: null });
    try {
      if (!searchTerm.trim()) {
        return;
      }

      const q = query(
        collection(firestore, COLLECTIONS.PILGRIMS),
        where('Name', '>=', searchTerm),
        where('Name', '<=', searchTerm + '\uf8ff')
      );

      const querySnapshot = await getDocs(q);
      const pilgrims: Record<string, Pilgrim> = {};

      querySnapshot.forEach((doc) => {
        pilgrims[doc.id] = {
          ...doc.data(),
          id: doc.id,
        } as Pilgrim;
      });

      set({ pilgrims, loading: false });
    } catch (error: any) {
      console.error('Error searching pilgrims:', error);
      set({ error: error.message, loading: false });
    }
  },
}));
